// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { sortBy, keyBy } from 'lodash'
import { createSelector } from 'reselect'

import { nextId } from '../store/newIds'
import { isSeries } from '../helpers/lines'
import { fullFileStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Line} Line
 */

/**
 * @type {function(PlottrState): Array<Line>}
 */
export const allLinesSelector = createSelector(fullFileStateSelector, ({ lines }) => lines ?? [])

/**
 * @type {function(PlottrState): Array<Line>}
 */
export const allSeriesLinesSelector = createSelector(allLinesSelector, (lines) =>
  lines.filter(isSeries)
)

/**
 * @type {function(PlottrState): Record<number, Line>}
 */
export const allLinesByIdSelector = createSelector(allLinesSelector, (lines) => {
  return keyBy(lines, 'id')
})

/**
 * @type {function(PlottrState): number}
 */
export const nextLineIdSelector = createSelector(allLinesSelector, (lines) => nextId(lines))

/**
 * @param {PlottrState} _state
 * @param {'series' | number} bookId
 * @return {'series' | number}
 */
const bookIdSelector = (_state, bookId) => bookId
/**
 * @type {function(PlottrState, number | 'series'): Array<Line>}
 */
export const linesForBookSelector = createSelector(
  allLinesSelector,
  bookIdSelector,
  (lines, bookId) => {
    return lines.filter((l) => l && l.bookId == bookId)
  }
)

/**
 * @type {function(PlottrState): (function('series' | number): Line)}
 */
export const firstLineForBookThunkSelector = createSelector(allLinesSelector, (lines) => {
  return (bookId) => {
    const linesInBook = lines.filter((l) => l && l.bookId == bookId)
    return sortBy(linesInBook, 'position')[0]
  }
})

/**
 * @type {function(PlottrState, number | 'series'): (Line | Null)}
 */
export const firstLineForBookSelector = createSelector(linesForBookSelector, (lines) => {
  return sortBy(lines, 'position')[0] ?? null
})

/**
 * @param {PlottrState} _state
 * @param {number} lineId
 * @return {number}
 */
const lineIdSelector = (_state, lineId) => {
  return lineId
}
/**
 * @type {function(PlottrState, number): (Line | Null)}
 */
export const singleLineSelector = createSelector(
  allLinesSelector,
  lineIdSelector,
  (lines, lineId) => {
    return (
      lines.find(({ id }) => {
        return id === lineId
      }) ?? null
    )
  }
)
